import axios from 'axios'
import type { AdminOrgStatus, AdminOverview, PaymentLogStats } from '@/types/admin'
import type { Deal } from '@/stores/deals'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5006/api/v1'

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30000,
  // NestJS @Query reads repeated params as arrays, but axios's default serializer emits
  // key[]=value for arrays. Flatten arrays into repeated ?key=a&key=b so array filters
  // (contactOwner, campaign, stageIds, etc.) reach the backend.
  paramsSerializer: {
    indexes: null,
  },
})

// Function to get auth header
function getAuthHeader() {
  const token = localStorage.getItem('accessToken')
  return token ? `Bearer ${token}` : null
}

// Multi-org active org for the x-org-id header, held in a module-scoped (per-TAB)
// variable instead of being read from localStorage on every request. localStorage is
// shared across all tabs, so a second tab switching orgs would otherwise hijack THIS
// tab's requests: the URL / active-org ref would still say org A while the header
// silently followed the other tab's localStorage to org B, producing wrong-org 401/404s
// ("failed loading") with no visible cause. The auth store keeps this in sync on every
// org change via setActiveOrg(). Seeded from localStorage for the first requests at boot,
// before the router guard has reconciled the URL's ?org=.
let requestActiveOrgId: string | null = localStorage.getItem('activeOrgId')

/** Set the org id sent as the x-org-id header on subsequent requests (per tab). */
export function setRequestActiveOrg(orgId: string | null): void {
  requestActiveOrgId = orgId
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const authHeader = getAuthHeader()

    if (authHeader) {
      config.headers = {
        ...config.headers,
        Authorization: authHeader
      } as any
    }

    // Multi-org: tell the API which organization this request operates against.
    // Reads the per-tab value (see setRequestActiveOrg) — NOT localStorage — so a
    // different org open in another tab can't redirect this tab's requests. The server
    // validates it against the user's memberships; switching orgs is just changing this
    // value (no re-login). Falls back to the token's org when unset.
    if (requestActiveOrgId) {
      config.headers = {
        ...config.headers,
        'x-org-id': requestActiveOrgId,
      } as any
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor — no token refresh; the access token is long-lived (7d).
// We only handle subscription lockout + demo-mode write blocks here.
api.interceptors.response.use(
  async (response) => {
    // The server's DemoModeInterceptor short-circuits mutations against the
    // demo org with a 200 + { demoMode: true, message: '...' }. Surface it as
    // a toast and reject so calling code's try/catch path runs (no optimistic
    // UI updates leak into the demo).
    const body = response.data
    if (body && body.demoMode === true && response.config?.method && response.config.method.toUpperCase() !== 'GET') {
      try {
        const { useToast } = await import('vue-toastification')
        useToast().info(body.message || "Demo mode — sign up to make changes.")
      } catch {
        // toast may not be available during prerender — that's fine
      }
      return Promise.reject({
        isDemoMode: true,
        message: body.message,
        response,
      })
    }
    return response
  },
  async (error) => {
    // Handle subscription lockout — show blocking overlay instead of redirecting
    if (error.response?.status === 403) {
      const code = error.response?.data?.code
      if (code === 'subscription_locked_out' || code === 'subscription_pending_selection') {
        const { useAuthStore } = await import('@/stores/auth')
        const authStore = useAuthStore()
        authStore.subscriptionLockedOut = error.response.data.message
          || 'Your account access is restricted. Contact your organization owner.'
        return Promise.reject(error)
      }
    }

    return Promise.reject(error)
  }
)

// API response types
export interface ApiResponse<T = any> {
  data: T
}

export interface ApiListResponse<T = any, S = unknown> {
  data: T[]
  meta: {
    page: number
    pageSize: number
    total: number
    hasNext: boolean
  }
  stats?: S
}

export interface ApiError {
  error: {
    code: string
    message: string
    details?: any
    requestId?: string
  }
}

// Generic API methods
export const apiClient = {
  get: <T = any>(url: string, params?: any): Promise<ApiResponse<T>> =>
    api.get(url, { params }).then(res => res.data),

  post: <T = any>(url: string, data?: any): Promise<ApiResponse<T>> =>
    api.post(url, data).then(res => res.data),

  put: <T = any>(url: string, data?: any): Promise<ApiResponse<T>> =>
    api.put(url, data).then(res => res.data),

  patch: <T = any>(url: string, data?: any): Promise<ApiResponse<T>> =>
    api.patch(url, data).then(res => res.data),

  delete: (url: string): Promise<void> =>
    api.delete(url).then(() => {}),

  getList: <T = any>(url: string, params?: any): Promise<ApiListResponse<T>> =>
    api.get(url, { params }).then(res => res.data),
}

// Specific API endpoints
export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),
    
  register: (userData: any) =>
    apiClient.post('/auth/register', userData),
    
  logout: () =>
    apiClient.post('/auth/logout'),
    
  me: () =>
    apiClient.get('/auth/me'),

  registerDeviceToken: (data: { token: string; platform: 'ios' | 'android' }) =>
    apiClient.post('/auth/device-token', data),

  removeDeviceToken: (token: string) =>
    api.delete('/auth/device-token', { data: { token } }).then(res => res.data),
}


/** AI lead qualification statuses the contacts list can filter on. `unscored` = never scored. */
export type ContactLeadStatusFilter = 'qualified' | 'nurture' | 'in_progress' | 'disqualified' | 'unscored'

/**
 * Query params of `GET /contacts` (and `GET /contacts/export`, which takes the same filters).
 * Every one is optional. Repeatable params (`leadStatus`, `source`, `contactOwner`, `campaign`,
 * `tags`) take arrays and are sent as repeated keys. Unlisted keys are allowed so older call
 * sites keep compiling.
 *
 * Modes (all optional; absent = `any` / `is`, today's meaning): `tagMode` is `any` | `all` |
 * `none`; the others are `is` | `is_not`. A negation also matches contacts with no value (untagged,
 * no owner, no company, no source, no campaign); `leadStatus` is not `unscored` leaves the
 * never-scored ones out. `contactOwnerMode` is admin-only (a non-admin stays on their own contacts).
 */
export interface ContactListParams {
  page?: number
  pageSize?: number
  search?: string
  /** `lastActivityAt` = last touch; contacts never touched sort last when newest first. */
  sortBy?: 'createdAt' | 'updatedAt' | 'firstName' | 'lastName' | 'priority' | 'lastActivityAt'
  sortOrder?: 'asc' | 'desc'
  includeCompany?: boolean
  includeOwner?: boolean
  includeHasDeal?: boolean
  /** AI lead qualification status; `unscored` also matches contacts with no qualification at all. */
  leadStatus?: ContactLeadStatusFilter | ContactLeadStatusFilter[]
  /** true = only contacts opted out of email; false = only those who are not. */
  emailOptOut?: boolean
  /** Exact, case-sensitive source match. */
  source?: string | string[]
  sourceMode?: 'is' | 'is_not'
  tagMode?: 'any' | 'all' | 'none'
  contactOwnerMode?: 'is' | 'is_not'
  /** One company id. */
  companyId?: string
  companyMode?: 'is' | 'is_not'
  leadStatusMode?: 'is' | 'is_not'
  campaignMode?: 'is' | 'is_not'
  [key: string]: unknown
}

export interface BulkTagsResult {
  updated: number
  failed: number
  affectedTagCount: number
}

/** One linked record in the owner-change preview (GET /contacts/:id/owner-related, /deals/:id/owner-related). */
export interface OwnerRow {
  id: string
  name: string
  kind: 'deal' | 'contact' | 'company'
  stageName?: string | null
  stageType?: string | null
  isArchived?: boolean
  ownerId: string | null
  ownerName: string | null
  /** The viewer may hand this record over: an admin, or its current owner. */
  canChange: boolean
}

export interface ContactOwnerRelated {
  company: OwnerRow | null
  deals: OwnerRow[]
}

export interface DealOwnerRelated {
  contact: OwnerRow | null
  company: OwnerRow | null
}

/** Rides on PATCH /contacts/:id with a changed `contactOwner`. */
export interface ContactOwnerCarryOver {
  dealIds?: string[]
  companyId?: string
}

/** Rides on PATCH /deals/:id with a changed `assignedTo`. */
export interface DealOwnerCarryOver {
  contact?: boolean
  company?: boolean
}

/** One stage of the Contacts report's "moved into" list (an arrival: each deal once per stage). */
export interface ContactsReportStage {
  pipelineId: string
  pipelineName: string
  stageId: string
  stageName: string
  stageType: string
  order: number
  count: number
}

/** `GET /contacts/report`: what the Contacts list's filters add up to in a date range. */
export interface ContactsReport {
  range: { from: string; to: string }
  /** `own`: the viewer is not org-wide, so deals and activities are their own (as the lists the figures link to). */
  scope: 'org' | 'own'
  contacts: { matching: number; assigned: number }
  deals: { created: number; won: number; lost: number; entered: ContactsReportStage[] }
  activity: { activities: number; calls: number }
}

export const contactsApi = {
  // The Contacts list's filters (every GET /contacts param, modes included) plus search, `from` and `to`.
  report: (params: Record<string, unknown>): Promise<{ data: ContactsReport }> =>
    apiClient.get<ContactsReport>('/contacts/report', params),

  list: (params?: ContactListParams) =>
    apiClient.getList('/contacts', params),

  get: (id: string, params?: any) =>
    apiClient.get(`/contacts/${id}`, params),

  create: (data: any) =>
    apiClient.post('/contacts', data),

  update: (id: string, data: any) =>
    apiClient.patch(`/contacts/${id}`, data),

  // The contact's company and deals with their owners (the owner-change dialog).
  ownerRelated: (id: string): Promise<{ data: ContactOwnerRelated }> =>
    apiClient.get<ContactOwnerRelated>(`/contacts/${id}/owner-related`),

  delete: (id: string, reason?: string) =>
    apiClient.delete(`/contacts/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),

  convertToDeal: (id: string, data?: any) =>
    apiClient.post(`/contacts/${id}/convert-to-deal`, data),

  getTimeline: (id: string, params?: any) =>
    apiClient.getList(`/contacts/${id}/timeline`, params),

  bulkUpdateOwner: (contactIds: string[], newOwnerId: string) =>
    apiClient.post('/contacts/bulk/update-owner', { contactIds, newOwnerId }),

  bulkUpdateSeen: (contactIds: string[], isSeen: boolean) =>
    apiClient.post('/contacts/bulk/update-seen', { contactIds, isSeen }),

  // Add and/or remove tags on many contacts. Admins can tag any contact; everyone else only
  // their own (403 when the selection includes one they do not own). Like the other bulk
  // endpoints the body is the bare result, not wrapped in `{ data }`, so it resolves to that.
  bulkUpdateTags: (contactIds: string[], addTags?: string[], removeTags?: string[]) =>
    apiClient.post('/contacts/bulk/tags', { contactIds, addTags, removeTags }) as unknown as Promise<BulkTagsResult>,

  markAsSeen: (id: string) =>
    apiClient.patch(`/contacts/${id}/mark-seen`),

  getFilterCampaigns: () =>
    apiClient.get('/contacts/filter-options/campaigns'),

  getTags: (q?: string) =>
    apiClient.get('/contacts/tags', q ? { q } : undefined),

  // Archive endpoints
  listArchived: (params?: any) =>
    apiClient.getList('/contacts/archive/list', params),

  getArchived: (id: string) =>
    apiClient.get(`/contacts/archive/${id}`),

  restoreArchived: (id: string) =>
    apiClient.post(`/contacts/archive/${id}/restore`),

  permanentlyDeleteArchived: (id: string) =>
    apiClient.delete(`/contacts/archive/${id}`),

  merge: (primaryId: string, mergeIds: string[]) =>
    apiClient.post('/contacts/merge', { primaryId, mergeIds }),
}

/** Customer = at least one payment or a deal in a won-type stage; everyone else is a prospect. */
export type CompanyLifecycle = 'customer' | 'prospect'

/** Most recent recurring line item on the company's won or open deals; `tier` is left out when it has none. */
export interface CompanyPlan {
  product: string
  tier?: string
}

/**
 * Per-row account stats of `GET /companies?includeStats=true`. Absent on a row when the stats
 * lookup failed (render dashes). `paidToDate` is 0 and `currency` null when nothing was paid.
 * `nextPaymentDate` is a `YYYY-MM-DD` CALENDAR date (parse it as a date, never `new Date(str)`,
 * which would shift it by the browser's timezone) or null: the earliest billing date among the
 * company's active deals in a billable Customer Support stage. It can be in the PAST, which
 * means the company is overdue; derive "overdue N days" from it. Figures follow what the viewer
 * may see: a non-admin's deal and payment figures only cover their own deals and payments, and
 * a readonly user gets no payment figures; `scoped` is true for such viewers so the UI can say
 * the numbers are partial.
 */
export interface CompanyStats {
  contactsCount: number
  openDealsCount: number
  paidToDate: number
  currency: string | null
  nextPaymentDate: string | null
  plan: CompanyPlan | null
  lifecycle: CompanyLifecycle
  /** true when the viewer is not an admin, i.e. the deal and payment figures are partial. Absent = false. */
  scoped?: boolean
}

/** Tab counts of the Companies list: over the visible, search/tag-filtered companies, ignoring `lifecycle`. */
export interface CompanySegments {
  all: number
  customer: number
  prospect: number
}

/** `GET /companies` response; `meta.segments` is only there with `includeStats=true`. */
export interface CompanyListResponse<T = any> extends ApiListResponse<T> {
  meta: ApiListResponse['meta'] & { segments?: CompanySegments }
}

/** One of the last six calendar months in `CompanySummary.paymentMonths`. */
export interface CompanyPaymentMonth {
  year: number
  /** 1-12 */
  month: number
  paid: boolean
  /** The month of `nextPaymentDate` (past or future) and still unpaid; only flagged inside the 6-month window. */
  due: boolean
}

/**
 * `GET /companies/:id/summary` (sent as `{ data }`). `paymentMonths` are the last 6 calendar
 * months, oldest first, and an EMPTY array when the user may not see payments (readonly): hide
 * the strip then. `monthly` is null without a recurring plan. `nextPaymentDate` is a `YYYY-MM-DD`
 * calendar date and may be in the past (overdue), even older than the window; see `CompanyStats`.
 */
export interface CompanySummary {
  lifecycle: CompanyLifecycle
  plan: CompanyPlan | null
  monthly: number | null
  currency: string | null
  paidToDate: number
  nextPaymentDate: string | null
  /**
   * The billable deal behind `nextPaymentDate` (the one a payment recorded now should advance);
   * null without one. Pass it as `dealId` when recording a payment from the company.
   */
  nextPaymentDealId?: string | null
  paymentMonths: CompanyPaymentMonth[]
  /** true when the viewer is not an admin, i.e. the figures are partial. Absent = false. */
  scoped?: boolean
}

/**
 * Query params of `GET /companies`. Every one is optional. `tags` takes an array and is sent as
 * repeated keys. Unlisted keys are allowed so older call sites keep compiling.
 */
export interface CompanyListParams {
  page?: number
  pageSize?: number
  search?: string
  industry?: string
  size?: string
  tags?: string[]
  sortBy?: 'createdAt' | 'updatedAt' | 'name' | 'contactCount'
  sortOrder?: 'asc' | 'desc'
  includeContactCount?: boolean
  /** Add `stats` to every row and `meta.segments`, batched over the page. */
  includeStats?: boolean
  /** Only customers or only prospects, paginated server-side. */
  lifecycle?: CompanyLifecycle
  /** Fetch every company without pagination (the legacy desktop load). */
  all?: boolean
  [key: string]: unknown
}

export const companiesApi = {
  list: (params?: CompanyListParams) =>
    apiClient.getList('/companies', params) as Promise<CompanyListResponse>,

  // Distinct company tags with counts; `q` narrows server-side (list is capped at 50).
  getTags: (q?: string) =>
    apiClient.get<{ tags: { tag: string; count: number }[] }>('/companies/tags', q ? { q } : undefined),
    
  get: (id: string, params?: any) =>
    apiClient.get(`/companies/${id}`, params),
    
  create: (data: any) =>
    apiClient.post('/companies', data),
    
  update: (id: string, data: any) =>
    apiClient.patch(`/companies/${id}`, data),
    
  delete: (id: string) =>
    apiClient.delete(`/companies/${id}`),
    
  getContacts: (id: string, params?: any) =>
    apiClient.getList(`/companies/${id}/contacts`, params),

  getSummary: (id: string) =>
    apiClient.get<CompanySummary>(`/companies/${id}/summary`),

  // "Activity at <company>": activities and notes on the company, its contacts and its deals, in
  // the shape and paging of `contactsApi.getTimeline`.
  getTimeline: (id: string, params?: any) =>
    apiClient.getList(`/companies/${id}/timeline`, params),
}

export const activitiesApi = {
  list: (params?: any) =>
    apiClient.getList('/activities', params),

  get: (id: string, params?: any) =>
    apiClient.get(`/activities/${id}`, params),

  create: (data: any) =>
    apiClient.post('/activities', data),

  update: (id: string, data: any) =>
    apiClient.patch(`/activities/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/activities/${id}`),

  updateStatus: (id: string, status: string) =>
    apiClient.patch(`/activities/${id}/status`, { status }),

  getUpcoming: (params?: any) =>
    apiClient.getList('/activities/upcoming', params),

  getOverdue: (params?: any) =>
    apiClient.getList('/activities/overdue', params),

  // Entity-specific endpoints
  getByContact: (contactId: string, params?: any) =>
    apiClient.getList(`/activities/contact/${contactId}`, params),

  getByDeal: (dealId: string, params?: any) =>
    apiClient.getList(`/activities/deal/${dealId}`, params),

  getByCompany: (companyId: string, params?: any) =>
    apiClient.getList(`/activities/company/${companyId}`, params),
}

export const recordingsApi = {
  list: (params?: any) =>
    apiClient.getList('/recordings', params),

  getStreamToken: (attachmentId: string) =>
    apiClient.post(`/attachments/${attachmentId}/stream-token`),

  listDials: (params?: any) =>
    apiClient.getList('/recordings/dials', params),

  dialsSummary: (params?: any) =>
    apiClient.get('/recordings/dials/summary', params),
}

/** Body of `POST /users/invite` (one person per call). */
export interface InviteUserPayload {
  email: string
  role: string
  name?: string
  /** Personal note shown in the invitation email, up to 500 characters. */
  note?: string
}

export const usersApi = {
  list: (params?: any) =>
    apiClient.getList('/users', params),
    
  get: (id: string, params?: any) =>
    apiClient.get(`/users/${id}`, params),
    
  create: (data: any) =>
    apiClient.post('/users', data),
    
  update: (id: string, data: any) =>
    apiClient.patch(`/users/${id}`, data),
    
  delete: (id: string) =>
    apiClient.delete(`/users/${id}`),
    
  invite: (data: InviteUserPayload) =>
    apiClient.post('/users/invite', data),
    
  resendInvitation: (id: string) =>
    apiClient.post(`/users/${id}/resend-invitation`),
    
  updateRole: (id: string, role: string) =>
    apiClient.patch(`/users/${id}/role`, { role }),
    
  activate: (id: string) =>
    apiClient.patch(`/users/${id}/activate`),
    
  deactivate: (id: string) =>
    apiClient.patch(`/users/${id}/deactivate`),
}

export const pipelinesApi = {
  list: (params?: any) =>
    apiClient.getList('/pipelines', params),

  get: (id: string) =>
    apiClient.get(`/pipelines/${id}`),

  create: (data: any) =>
    apiClient.post('/pipelines', data),

  update: (id: string, data: any) =>
    apiClient.patch(`/pipelines/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/pipelines/${id}`),

  reorderStages: (id: string, stages: any[]) =>
    apiClient.patch(`/pipelines/${id}/stages/reorder`, { stages }),

  assignUsers: (id: string, userIds: string[]) =>
    apiClient.post(`/pipelines/${id}/assign`, { userIds }),

  unassignUsers: (id: string, userIds: string[]) =>
    apiClient.post(`/pipelines/${id}/unassign`, { userIds }),

  getAssignedUsers: (id: string) =>
    apiClient.get(`/pipelines/${id}/assigned-users`),

  /** Deals per stage (non-deleted, archived included); every stage listed, 0 when empty. Admin/owner. */
  stageDealCounts: (id: string) =>
    apiClient.get<{ stageId: string; deals: number }[]>(`/pipelines/${id}/stage-deal-counts`),

  /**
   * Delete a stage. A stage with deals needs `moveTo` (another stage of the same pipeline) or the
   * API answers 409; its deals and their board positions move there first. Resolves to the
   * updated pipeline and how many deals moved. Uses the axios instance because
   * `apiClient.delete` drops the response body.
   */
  deleteStage: <P = any>(id: string, stageId: string, moveTo?: string) =>
    api
      .delete(`/pipelines/${id}/stages/${stageId}`, { params: moveTo ? { moveTo } : undefined })
      .then(res => res.data as { data: P; movedDeals: number }),
}

// Query for reportsApi.salesReport (GET /reports/sales/full); response shapes in @/types/reports.
export type { SalesReportParams } from '@/types/reports'
// Used by reportsApi.kamHealthByKam and reportsApi.myBook.
import type { KamHealthByKam, KamReportParams, MyBook } from '@/types/reports'

/** One active org member's day in `GET /reports/team-day`. */
export interface TeamDayPerson {
  userId: string
  name: string
  /** Open activities due today (client zone). */
  dueToday: number
  /** Open activities due before today. */
  overdue: number
  /** Activities marked done today (status done, last updated today). */
  doneToday: number
}

/** An open activity due today: todayModel's `AgendaSource` plus its owner. */
export interface TeamDayItem {
  _id: string
  title: string
  type: string
  status: 'open'
  /** ISO instant. */
  dueAt: string
  ownerId: string
  ownerName: string
  contact: { _id: string; firstName?: string; lastName?: string } | null
  company: { _id: string; name?: string } | null
}

/**
 * `GET /reports/team-day` (sent as `{ data }`; admin, super admin, owner, root; everyone else
 * gets 403). Over the org's active members: `people` lists only those with something due,
 * overdue or done, the caller first, then by `dueToday + overdue`; `items` are today's open
 * activities, the caller's first, then by `dueAt`, at most 20.
 */
export interface TeamDay {
  /** Today, YYYY-MM-DD in the client zone. */
  date: string
  people: TeamDayPerson[]
  items: TeamDayItem[]
}

export const reportsApi = {
  pipelineSummary: (params?: any) =>
    apiClient.get('/reports/pipeline-summary', params),

  wonLost: (params?: any) =>
    apiClient.get('/reports/won-lost', params),

  dashboardStats: () =>
    apiClient.get('/reports/dashboard-stats'),

  periodSummary: (params?: { from?: string; to?: string }) =>
    apiClient.get('/reports/period-summary', params),

  forecast: (params?: { months?: number }) =>
    apiClient.get('/reports/forecast', params),

  salesReport: (params?: any) =>
    apiClient.get('/reports/sales/full', params),

  kamReport: (params?: any) =>
    apiClient.get('/reports/kam/full', params),

  kamPerformance: (params?: { period?: 7 | 30 | 90 }) =>
    apiClient.get('/reports/kam/performance', params),

  kamAccountHealth: (params?: any) =>
    apiClient.get('/reports/kam/account-health', params),

  /** Healthy / at risk / inactive companies per KAM now (admin, owner); the range does not change it. */
  kamHealthByKam: (params?: Pick<KamReportParams, 'period' | 'from' | 'to' | 'kamId'>) =>
    apiClient.get<KamHealthByKam>('/reports/kam/health-by-kam', params),

  dealCadence: (params?: {
    view?: 'rhythm' | 'age'
    page?: number
    pageSize?: number
    pipelineId?: string
    ownerId?: string
    severity?: 'never' | 'stale' | 'cold' | 'watch' | 'on_track'
    sortBy?: 'neglect' | 'name' | 'owner' | 'touches' | 'gap' | 'lastTouch' | 'overdue'
    sortOrder?: 'asc' | 'desc'
  }) => apiClient.get('/reports/deal-cadence', params),

  dailyTimeline: (params?: {
    date?: string
    ownerId?: string
    tzOffsetMinutes?: number
    minGapMinutes?: number
  }) => apiClient.get('/reports/daily-timeline', params),

  dealCadenceByOwner: (params?: { view?: 'rhythm' | 'age' }) =>
    apiClient.get('/reports/deal-cadence/by-owner', params),

  dealCadenceTouches: (dealId: string) =>
    apiClient.get('/reports/deal-cadence/touches', { dealId }),

  sendWeeklyEmail: () =>
    apiClient.post('/reports/send-weekly-email'),

  globalSearch: (q: string, limit?: number) =>
    apiClient.get('/reports/search', { q, limit }),

  /** The team's day for an admin's Today; `tzOffsetMinutes` = -new Date().getTimezoneOffset(). */
  teamDay: (params: { tzOffsetMinutes: number }) =>
    apiClient.get<TeamDay>('/reports/team-day', params),

  /**
   * My own book of accounts for the KAM Today page (member, KAM and above; readonly gets 403).
   * "This month" and "today" default to the browser's zone.
   */
  myBook: (params: { tzOffsetMinutes?: number } = {}) =>
    apiClient.get<MyBook>('/reports/my-book', {
      tzOffsetMinutes: -new Date().getTimezoneOffset(),
      ...params,
    }),
}

export const notesApi = {
  list: (params?: any) =>
    apiClient.getList('/notes', params),

  get: (id: string, params?: any) =>
    apiClient.get(`/notes/${id}`, params),

  create: (data: any) =>
    apiClient.post('/notes', data),

  update: (id: string, data: any) =>
    apiClient.patch(`/notes/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/notes/${id}`),

  getByEntity: (entityType: string, entityId: string, params?: any) =>
    apiClient.getList(`/notes/${entityType}/${entityId}`, params),

  getByContact: (contactId: string, params?: any) =>
    apiClient.getList(`/notes/contact/${contactId}`, params),

  getByDeal: (dealId: string, params?: any) =>
    apiClient.getList(`/notes/deal/${dealId}`, params),

  getByCompany: (companyId: string, params?: any) =>
    apiClient.getList(`/notes/company/${companyId}`, params),
}

export const attachmentsApi = {
  list: (params?: any) =>
    apiClient.getList('/attachments', params),
    
  upload: (file: File, entityType: string, entityId: string) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('entityType', entityType)
    formData.append('entityId', entityId)
    
    return api.post('/attachments/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data)
  },
  
  download: (id: string) =>
    api.get(`/attachments/${id}/download`, { responseType: 'blob' }),
    
  delete: (id: string) =>
    apiClient.delete(`/attachments/${id}`),
    
  getByEntity: (entityType: string, entityId: string, params?: any) =>
    apiClient.getList(`/attachments/${entityType}/${entityId}`, params),
}

export const organizationsApi = {
  get: (id: string) =>
    apiClient.get(`/organizations/${id}`),

  update: (id: string, data: any) =>
    apiClient.patch(`/organizations/${id}`, data),

  updateSettings: (id: string, data: any) =>
    apiClient.patch(`/organizations/${id}/settings`, data),

  addDomain: (id: string, domain: string) =>
    apiClient.post(`/organizations/${id}/domains`, { domain }),

  removeDomain: (id: string, domain: string) =>
    apiClient.delete(`/organizations/${id}/domains/${domain}`),

  deleteAccount: (id: string, confirmation: string) =>
    apiClient.post(`/organizations/${id}/delete-account`, { confirmation }),
}

export const subscriptionsApi = {
  getCurrent: () =>
    apiClient.get('/subscriptions/current'),

  getPaddleConfig: () =>
    apiClient.get('/subscriptions/paddle-config'),

  prepareCheckout: (withAiAddon = false) =>
    apiClient.post('/subscriptions/prepare-checkout', { withAiAddon }),

  getAiUsage: () =>
    apiClient.get('/subscriptions/ai-usage'),

  previewAiAddon: (enable: boolean) =>
    apiClient.post('/subscriptions/ai-addon/preview', { enable }),

  setAiAddon: (enable: boolean) =>
    apiClient.post('/subscriptions/ai-addon', { enable }),

  setAiOverageCap: (blocks: number) =>
    apiClient.post('/subscriptions/ai-addon/cap', { blocks }),

  cancel: () =>
    apiClient.post('/subscriptions/cancel'),

  resume: () =>
    apiClient.post('/subscriptions/resume'),

  canAddUser: () =>
    apiClient.get('/subscriptions/can-add-user'),

  getPaymentMethodTransaction: () =>
    apiClient.get('/subscriptions/payment-method-transaction'),

  selectActiveUsers: (userIds: string[]) =>
    apiClient.post('/subscriptions/select-active-users', { userIds }),
}

/** Query params of `GET /payment-history`, all optional; unlisted keys are allowed. */
export interface PaymentHistoryListParams {
  page?: number
  pageSize?: number
  search?: string
  companyId?: string
  /** Only the payments recorded against this deal (the Deal page's payments card). */
  dealId?: string
  /** Admins: the payments on this person's book (their deals, their contacts' deals, their companies, the ones they recorded). */
  ownerId?: string
  /** Admins: only the payments this person recorded. */
  recordedBy?: string
  dateRange?: string
  /** `YYYY-MM`: only the payments for that period (the payment's `year` + `month`). */
  month?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  [key: string]: unknown
}

/** What a payment was for: the paid deal's most recent line item; `tier` null without tiers. */
export interface PaymentProduct {
  name: string
  tier: string | null
}

/** A row of `GET /payment-history`. */
export interface PaymentHistoryRow {
  id: string
  /** Absent when the deal or the author's account was deleted. */
  dealId?: string
  dealName?: string
  /** The payment names a deal that has since been deleted (`dealId` is its old id). */
  dealMissing?: boolean
  companyId: string
  companyName: string
  year: number
  /** "01".."12" */
  month: string
  amount: number
  currency: string
  paymentMethod?: string
  referenceNumber?: string
  notes?: string
  createdBy?: string
  createdByName?: string
  createdAt: string
  /** Null when the payment has no deal or the deal has no line items. */
  product?: PaymentProduct | null
}

/** One currency's total in `GET /payment-history` `meta.totalsByCurrency`. */
export interface PaymentCurrencyTotal {
  currency: string
  amount: number
}

/** `meta` of `GET /payment-history`: totals cover every row matching the filters, not the page. */
export interface PaymentHistoryListMeta {
  page: number
  pageSize: number
  total: number
  hasNext: boolean
  /** Every amount added up, whatever its currency (older callers). */
  totalAmount: number
  /** The same set per currency, biggest first (a payment without one counts as BDT). Absent on older APIs. */
  totalsByCurrency?: PaymentCurrencyTotal[]
}

/**
 * Which billable deals `GET /payment-history/due-summary` lists: `overdue` (default) = billing
 * date passed; `due` = the rest of `month`, not yet passed; `all` = both.
 */
export type DueScope = 'overdue' | 'due' | 'all'

/**
 * Query params of `GET /payment-history/due-summary`, all optional; unlisted keys are allowed.
 * `meta.total` and `meta.totalDueAmount` cover every matching row, not just the page.
 */
export interface DueSummaryParams {
  page?: number
  pageSize?: number
  /** Admins only: deals assigned to this user. Others always see their own. */
  ownerId?: string
  scope?: DueScope
  /** `YYYY-MM`, the window for `due` and `all`; default the current (UTC) month. */
  month?: string
  [key: string]: unknown
}

export const paymentHistoryApi = {
  list: (params?: PaymentHistoryListParams) =>
    apiClient.get('/payment-history', params),

  create: (data: any) =>
    apiClient.post('/payment-history', data),

  update: (id: string, data: any) =>
    apiClient.patch(`/payment-history/${id}`, data),

  dueSummary: (params?: DueSummaryParams) =>
    apiClient.get('/payment-history/due-summary', params),
}

/** Stage lifecycle type, the source of truth for "open / won / lost" (deal `status` is not maintained). */
export type DealStageType = 'open' | 'won' | 'lost'

/**
 * Whether a call was received or placed. The API sends it on call activities (activity responses and
 * contact timeline items) only when the call recorded one, so every reader must cope with it absent.
 */
export type ActivityDirection = 'inbound' | 'outbound'

/**
 * The deal an activity response links to (`deal` on `GET /activities/:id?includeRelated=true` and on
 * list reads). `stageId` / `pipelineId` are the deal's current stage and pipeline; each is left out
 * when the deal has none, or its stage no longer exists. Name the stage with the pipelines store.
 */
export interface ActivityDealRef {
  _id: string
  name: string
  stageId?: string
  pipelineId?: string
}

/**
 * Query params of `GET /deals` (and `GET /deals/export`, which takes the same filters).
 * Every one is optional. Repeatable params (`stageIds`, `tag`, `leadStatus`) take arrays and are
 * sent as repeated keys. Unlisted keys are allowed so older call sites keep compiling.
 *
 * Modes (all optional; absent = `any` / `is`, today's meaning): `tagMode` is `any` | `all` | `none`;
 * the others are `is` | `is_not`. A negation also matches deals with no value (untagged, no owner,
 * no pipeline, no contact). `assignedToMode` is admin-only (a non-admin stays on their own deals).
 */
export interface DealListParams {
  page?: number
  pageSize?: number
  search?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  assignedTo?: string
  assignedToMode?: 'is' | 'is_not'
  pipelineId?: string
  pipelineMode?: 'is' | 'is_not'
  stageIds?: string[]
  stageMode?: 'is' | 'is_not'
  /** One company id. */
  companyId?: string
  companyMode?: 'is' | 'is_not'
  /**
   * Lifecycle type of the deal's stage. `open` also includes deals in stages with no type and
   * deals with no stage; `won` / `lost` are exact. Counts follow it (`meta.total`).
   */
  stageType?: DealStageType
  noActivity?: boolean
  lastActivityAfter?: string
  lastActivityBefore?: string
  createdAfter?: string
  createdBefore?: string
  filterPriority?: string
  tag?: string[]
  tagMode?: 'any' | 'all' | 'none'
  leadStatus?: string | string[]
  leadStatusMode?: 'is' | 'is_not'
  archived?: 'only' | 'all'
  [key: string]: unknown
}

export type DealMergeSide = 'survivor' | 'other'
export type DealMergeValuePick = DealMergeSide | 'lineItems'

/** Per-field picks for POST /deals/merge; a field left out keeps the survivor's value. */
export interface DealMergePicks {
  name?: DealMergeSide
  stage?: DealMergeSide
  value?: DealMergeValuePick
  owner?: DealMergeSide
  contact?: DealMergeSide
  company?: DealMergeSide
  expectedCloseDate?: DealMergeSide
  priority?: DealMergeSide
  source?: DealMergeSide
}

export interface DealMergeMoves {
  activities: number
  calls: number
  recordings: number
  notes: number
  files: number
  lineItems: number
  payments: number
  emailEnrollments: number
  whatsappConversations: number
  whatsappEnrollments: number
  boardCard: boolean
}

/** One deal as the merge preview lays it out (GET /deals/:id/merge-preview). */
export interface DealMergePreviewSide {
  _id: string
  name: string
  pipelineId: string | null
  pipelineName: string | null
  stageId: string | null
  stageName: string | null
  stageType: string | null
  value: number
  currency: string | null
  assignedTo: { _id: string; name: string | null } | null
  contactId: string | null
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  companyId: string | null
  companyName: string | null
  expectedCloseDate: string | null
  priority: string | null
  source: string | null
  tags: string[]
  isArchived: boolean
  createdAt: string | null
  lineItems: number
}

export interface DealMergePreview {
  survivor: DealMergePreviewSide
  other: DealMergePreviewSide
  moves: DealMergeMoves
  lineItemsTotal: number | null
  warnings: { code: string; message: string }[]
  canMerge: boolean
  reason: string | null
}

export interface DealMergeResult {
  survivorId: string
  mergedId: string
  moved: DealMergeMoves
  alreadyMerged: boolean
}

export const dealsApi = {
  list: (params?: DealListParams) =>
    apiClient.getList('/deals', params),

  get: (id: string, params?: any) =>
    apiClient.get(`/deals/${id}`, params),

  // Every deal of one contact (the Contact page's Deals cards): newest first, at most 50, archived
  // ones included (`isArchived`). Not owner-scoped, unlike `list({ contactId })`, so a member sees
  // all of a contact's deals. Rows carry `nextStep`, `plan`, `leadQualification` and `stageEnteredAt`.
  listForContact: (contactId: string): Promise<{ data: Deal[] }> =>
    apiClient.get<Deal[]>(`/deals/contact/${contactId}`),

  // Merge two deals: the preview lays both side by side with what would move; the merge keeps the
  // survivor and archives the other (no undo). Retry with the same picks after a failure.
  mergePreview: (survivorId: string, otherId: string): Promise<{ data: DealMergePreview }> =>
    apiClient.get<DealMergePreview>(`/deals/${survivorId}/merge-preview`, { with: otherId }),

  merge: (survivorId: string, otherId: string, pick?: DealMergePicks): Promise<{ data: DealMergeResult }> =>
    apiClient.post<DealMergeResult>('/deals/merge', { survivorId, otherId, ...(pick ? { pick } : {}) }),

  // The deal's contact and company with their owners (the owner-change dialog).
  ownerRelated: (id: string): Promise<{ data: DealOwnerRelated }> =>
    apiClient.get<DealOwnerRelated>(`/deals/${id}/owner-related`),

  create: (data: any) =>
    apiClient.post('/deals', data),

  update: (id: string, data: any) =>
    apiClient.patch(`/deals/${id}`, data),

  delete: (id: string, reason?: string) =>
    apiClient.delete(`/deals/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),

  addToPipeline: (id: string, data: any) =>
    apiClient.post(`/deals/${id}/topipeline`, data),

  bulkUpdateOwner: (dealIds: string[], newOwnerId: string) =>
    apiClient.post('/deals/bulk/update-owner', { dealIds, newOwnerId }),

  bulkUpdateStage: (dealIds: string[], stageId: string) =>
    apiClient.post('/deals/bulk/update-stage', { dealIds, stageId }),

  bulkArchive: (dealIds: string[]) =>
    apiClient.post('/deals/bulk/archive', { dealIds }),

  bulkUpdateTags: (dealIds: string[], addTags?: string[], removeTags?: string[]) =>
    apiClient.post('/deals/bulk/tags', { dealIds, addTags, removeTags }),

  // Distinct tag list across the org — used by the tag-filter dropdown.
  listTags: () => apiClient.get('/deals/tags'),

  // Per-stage counts of archived deals — drives the Archived tab's summary strip.
  archivedStageSummary: (pipelineId?: string) =>
    apiClient.get<{ stageId: string; stageName: string; pipelineName: string; count: number }[]>(
      '/deals/archived/stage-summary',
      pipelineId ? { pipelineId } : undefined,
    ).then(r => r.data),

  // Restore archived deals back onto the board. Distinct from `restoreArchived` below,
  // which resurrects a DELETED deal out of the `dealarchives` graveyard collection.
  bulkUnarchive: (dealIds: string[]) =>
    apiClient.post('/archive-rules/deals/unarchive', { dealIds }) as unknown as Promise<{
      updated: number
      failed: number
    }>,

  // Deleted-deal endpoints (the `dealarchives` graveyard reached by DELETE /deals/:id).
  // Named "archive" on the wire for backwards compatibility; surfaced in the UI as
  // "Deleted Deals" so it is not confused with the board archive above.
  listArchived: (params?: any) =>
    apiClient.getList('/deals/archive/list', params),

  getArchived: (id: string) =>
    apiClient.get(`/deals/archive/${id}`),

  restoreArchived: (id: string) =>
    apiClient.post(`/deals/archive/${id}/restore`),

  permanentlyDeleteArchived: (id: string) =>
    apiClient.delete(`/deals/archive/${id}`),
}

export interface ArchivePreviewStage {
  stageId: string
  stageName: string | null
  stageMissing: boolean
  deals: number
  contactsTagged: number
  contactTags: string[]
}

export interface ArchivePreview {
  totalDeals: number
  totalContactsTagged: number
  stages: ArchivePreviewStage[]
}

export interface ArchiveRunResult extends ArchivePreview {
  positionsRemoved: number
}

export type ArchiveRuleType = 'stage' | 'ai_review'

/** An AI review rule's scoring run. Counters are per deal row. Poll the rule while `state !== 'idle'`. */
export interface AiReviewRun {
  runId: string
  state: 'idle' | 'scoring' | 'cancelling'
  startedAt?: string
  heartbeatAt?: string
  finishedAt?: string
  total: number
  done: number
  failed: number
  byOutcome: Record<string, number>
}

export interface ArchiveRule {
  _id: string
  name: string
  /** Missing on rules written before AI review existed: those are stage rules (`ruleKind`). */
  type?: ArchiveRuleType
  pipelineId: string
  stages: { stageId: string; contactTags: string[] }[]
  /** AI review rules only. `touchedBefore` is an ISO date whose calendar day is read from its UTC fields. */
  aiReview?: { stageIds: string[]; touchedBefore: string; contactTags: string[] } | null
  aiReviewRun?: AiReviewRun | null
  enabled: boolean
  /** True for the length of a run: a second Run is refused (409) until it finishes. */
  running?: boolean
  lastRunAt?: string
  lastRunCounts?: { dealsArchived: number; contactsTagged: number } | null
}

export type AiReviewOutcome = 'qualified' | 'nurture' | 'in_progress' | 'disqualified'
export type AiReviewResultStatus = 'pending' | 'processing' | 'scored' | 'failed' | 'skipped' | 'archived' | 'dropped'
export type AiReviewDropReason =
  | 'touched'
  | 'rescored'
  | 'overridden'
  | 'contact_gone'
  | 'stage_changed'
  | 'archived_or_deleted'

/** Counts only, nothing sent to the AI. `cutoff` is null when the rule has no valid date. */
export interface AiReviewPreview {
  deals: number
  contacts: number
  byStage: { stageId: string; stageName: string; deals: number }[]
  skippedNoContact: number
  skippedNextStep: number
  skippedLiveElsewhere: number
  alreadyReviewed: number
  noAnswerOnly: number
  estimatedUsd: number
  cutoff: string | null
  timeZone: string
}

export interface AiReviewResultRow {
  dealId: string | null
  dealName: string | null
  contactId: string | null
  contactName: string | null
  stageName: string | null
  score: number | null
  outcome: AiReviewOutcome | null
  reason: string | null
  nextAction: string | null
  status: AiReviewResultStatus
  dropReason?: AiReviewDropReason | null
  scoredAt: string | null
}

export interface AiReviewResults {
  rows: AiReviewResultRow[]
  total: number
  byOutcome: Record<string, number>
  byStatus: Record<string, number>
}

export interface AiReviewArchiveResult {
  archived: number
  dropped: number
  dropReasons: Partial<Record<AiReviewDropReason, number>>
  contactsTagged: number
}

// Admin-only bulk archive tooling (Settings → Data Cleanup).
// CRUD responses are wrapped in { data } like the other resource controllers; the
// preview/run actions return their result object directly, like the bulk deal endpoints.
export const archiveRulesApi = {
  list: () => apiClient.get<ArchiveRule[]>('/archive-rules').then(r => r.data),

  get: (id: string) => apiClient.get<ArchiveRule>(`/archive-rules/${id}`).then(r => r.data),

  create: (data: any) => apiClient.post<ArchiveRule>('/archive-rules', data).then(r => r.data),

  update: (id: string, data: any) =>
    apiClient.patch<ArchiveRule>(`/archive-rules/${id}`, data).then(r => r.data),

  remove: (id: string) => apiClient.delete(`/archive-rules/${id}`),

  // Counts only — no writes. Drives the confirmation screen.
  preview: (id: string) =>
    apiClient.post(`/archive-rules/${id}/preview`) as unknown as Promise<ArchivePreview>,

  run: (id: string) =>
    apiClient.post(`/archive-rules/${id}/run`) as unknown as Promise<ArchiveRunResult>,

  // AI review rules (type 'ai_review'). Like preview/run, these return their result directly.
  // Every route but cancel answers 403 without the AI add-on or a configured AI.
  aiReview: {
    /** Free: counts and the estimated cost, nothing is sent to the AI. */
    preview: (id: string) =>
      apiClient.post(`/archive-rules/${id}/ai-review/preview`) as unknown as Promise<AiReviewPreview>,
    /** Starts scoring in the background. `runId: null, total: 0` means nothing was eligible. 409 while scoring. */
    score: (id: string) =>
      apiClient.post(`/archive-rules/${id}/ai-review/score`) as unknown as Promise<{ runId: string | null; total: number }>,
    cancel: (id: string) =>
      apiClient.post(`/archive-rules/${id}/ai-review/cancel`) as unknown as Promise<{ state: 'cancelling' | 'idle' }>,
    results: (
      id: string,
      params: { outcome?: AiReviewOutcome; status?: AiReviewResultStatus; page?: number; pageSize?: number } = {},
    ) => apiClient.get(`/archive-rules/${id}/ai-review/results`, params) as unknown as Promise<AiReviewResults>,
    /** Archives the deals rated Disqualified that are still stale and unchanged. 409 while an archive runs. */
    archive: (id: string) =>
      apiClient.post(`/archive-rules/${id}/ai-review/archive`) as unknown as Promise<AiReviewArchiveResult>,
  },
}

/** A contact field a CSV column can feed in the contacts import (the API's `ContactImportField`). */
export type ContactImportField =
  | 'firstName'
  | 'lastName'
  | 'fullName'
  | 'email'
  | 'phone'
  | 'mobile'
  | 'whatsapp'
  | 'jobTitle'
  | 'companyName'
  | 'tags'
  | 'source'
  | 'description'
  | 'priority'

/** How sure a suggested column mapping is. A mapping the user chose is always `exact`; a column left out is `skipped`. */
export type ContactImportConfidence = 'exact' | 'likely' | 'check' | 'skipped'

/** What you send back to the API: file header -> the field it feeds, or `null` for no field. */
export type ContactImportMapping = Record<string, ContactImportField | null>

/** One column of the uploaded file and the contact field it feeds (`null`: no field). */
export interface ContactImportMappingEntry {
  /** The header as written in the file; the key to use in a mapping sent back to the API. */
  header: string
  target: ContactImportField | null
  confidence: ContactImportConfidence
  /**
   * Set on a column with no field that the import still stores anyway: 'Remarks', 'Email opt-out',
   * 'Home phone' or 'Custom field'. Show "Kept as <keptAs>" instead of "Skipped"; sending `null` for it does
   * not drop it, sending a field replaces it. Absent on a column that is really not imported.
   */
  keptAs?: string
}

/** `POST /import/contacts/preview`: what the import would do. Nothing is written. */
export interface ContactImportPreview {
  fileName: string
  totalRows: number
  headers: string[]
  /** The first three rows; every header is a key ('' for an empty cell). */
  sampleRows: Record<string, string>[]
  /** One entry per header, in file order: the API's suggestion with the mapping you sent laid over it. */
  mapping: ContactImportMappingEntry[]
  /** `new + duplicates + errors === totalRows`. */
  stats: { new: number; duplicates: number; errors: number }
  /** The first 50 rows that cannot be imported (`row` is the spreadsheet row, the header being row 1). */
  errorRows: { row: number; message: string }[]
}

/**
 * What to do with a row that matches an existing contact (same email, or same phone): `update` writes the
 * row's non-empty values onto it (nothing cleared, phones and tags merged, owner kept), `update_empty` fills
 * its blank fields, `skip` leaves it, `create` imports the row as a new contact.
 */
export type ContactImportDuplicateMode = 'skip' | 'update' | 'update_empty' | 'create'

/**
 * Optional request settings for the CSV calls: `signal` cancels a preview the user has already
 * changed again; `timeout` (ms) replaces the 30s default for a large file's synchronous import.
 */
export interface CsvRequestConfig {
  signal?: AbortSignal
  timeout?: number
}

export const csvApi = {
  import: (entity: 'contacts' | 'companies' | 'deals', file: File, config: CsvRequestConfig = {}) => {
    const formData = new FormData()
    formData.append('file', file)

    return api.post(`/import/${entity}`, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data)
  },

  getImportJob: (jobId: string) =>
    api.get(`/import/jobs/${jobId}`).then(res => res.data),

  /**
   * Contacts import, step "Map columns": suggested (or the given) mapping, sample rows and counts of new
   * contacts, duplicates and rows with errors. `mapping` is header -> field (`null`: no field; a column the
   * import keeps anyway, see `keptAs`, stays kept); headers it does not name keep their suggestion.
   * Resolves to the response body: `{ data }`.
   */
  previewContacts: (file: File, mapping?: ContactImportMapping, config: CsvRequestConfig = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    if (mapping) {
      formData.append('mapping', JSON.stringify(mapping))
    }

    return api.post<{ data: ContactImportPreview }>('/import/contacts/preview', formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data)
  },

  /**
   * Contacts import with the mapping flow's choices. Every option is optional; with none this is the same
   * call as `import('contacts', file)`. `duplicateMode` defaults to `skip` on the server once any option is
   * sent; `ownerId` is honoured for admins only (an active member of the org, else 400); `source` fills rows
   * that have none. Resolves to the response body: `{ job }`.
   */
  importContactsMapped: (
    file: File,
    options: {
      mapping?: ContactImportMapping
      duplicateMode?: ContactImportDuplicateMode
      ownerId?: string
      source?: string
    } = {},
    config: CsvRequestConfig = {},
  ) => {
    const formData = new FormData()
    formData.append('file', file)
    if (options.mapping) {
      formData.append('mapping', JSON.stringify(options.mapping))
    }
    if (options.duplicateMode) {
      formData.append('duplicateMode', options.duplicateMode)
    }
    if (options.ownerId) {
      formData.append('ownerId', options.ownerId)
    }
    if (options.source) {
      formData.append('source', options.source)
    }

    return api.post('/import/contacts', formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data)
  },

  // Facebook CSV Import
  previewFacebookCsv: (file: File, companyKeywords?: string[], config: CsvRequestConfig = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    if (companyKeywords && companyKeywords.length > 0) {
      formData.append('companyKeywords', JSON.stringify(companyKeywords))
    }

    return api.post('/import/facebook/preview', formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data)
  },

  importFacebookContacts: (file: File, companyKeywords?: string[], config: CsvRequestConfig = {}) => {
    const formData = new FormData()
    formData.append('file', file)
    if (companyKeywords && companyKeywords.length > 0) {
      formData.append('companyKeywords', JSON.stringify(companyKeywords))
    }

    return api.post('/import/facebook/contacts', formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data)
  },
}

import type { ProductOpenDeals, ProductStats } from '@/types/products'
import type { NotificationCounts } from '@/types/notifications'

export const productsApi = {
  list: (params?: any) =>
    apiClient.getList('/products', params),

  /**
   * Customers, open deals and collected this month per product and tier (readonly and up).
   * "This month" defaults to the browser's zone.
   */
  stats: (params: { tzOffsetMinutes?: number } = {}) =>
    apiClient.get<ProductStats>('/products/stats', {
      tzOffsetMinutes: -new Date().getTimezoneOffset(),
      ...params,
    }),

  /** Open deals carrying a product, most recently updated first (`limit` 1..100, default 4). */
  openDeals: (id: string, params?: { limit?: number }) =>
    apiClient.get<ProductOpenDeals>(`/products/${id}/deals`, params),

  get: (id: string) =>
    apiClient.get(`/products/${id}`),

  create: (data: any) =>
    apiClient.post('/products', data),

  update: (id: string, data: any) =>
    apiClient.patch(`/products/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/products/${id}`),

  addTier: (productId: string, data: any) =>
    apiClient.post(`/products/${productId}/tiers`, data),

  updateTier: (productId: string, tierId: string, data: any) =>
    apiClient.patch(`/products/${productId}/tiers/${tierId}`, data),

  removeTier: (productId: string, tierId: string) =>
    apiClient.delete(`/products/${productId}/tiers/${tierId}`),
}

export const notificationsApi = {
  /**
   * The caller's notification counts: all, unread, and per category (mentions, replies,
   * assignments, system). The API answers `{ data: counts }` and `apiClient.get<T>` resolves to
   * that body, so `response.data` is the counts themselves.
   */
  counts: () =>
    apiClient.get<NotificationCounts>('/notifications/counts'),
}

export const dealLineItemsApi = {
  list: (dealId: string) =>
    apiClient.get(`/deals/${dealId}/line-items`),

  add: (dealId: string, data: any) =>
    apiClient.post(`/deals/${dealId}/line-items`, data),

  update: (dealId: string, lineItemId: string, data: any) =>
    apiClient.patch(`/deals/${dealId}/line-items/${lineItemId}`, data),

  remove: (dealId: string, lineItemId: string) =>
    apiClient.delete(`/deals/${dealId}/line-items/${lineItemId}`),

  reorder: (dealId: string, orderedIds: string[]) =>
    apiClient.patch(`/deals/${dealId}/line-items/reorder`, { orderedIds }),
}

export const apiKeysApi = {
  list: () =>
    apiClient.get('/api-keys'),

  create: (name: string, userId?: string) =>
    apiClient.post('/api-keys', { name, ...(userId ? { userId } : {}) }),

  update: (id: string, data: { name?: string; active?: boolean }) =>
    apiClient.patch(`/api-keys/${id}`, data),

  remove: (id: string) =>
    apiClient.delete(`/api-keys/${id}`),

  regenerate: (id: string) =>
    apiClient.post(`/api-keys/${id}/regenerate`),
}

// Audit Logs API endpoints (super_admin and owner only)
export const auditLogsApi = {
  list: (params?: any) =>
    apiClient.getList('/audit-logs', params),

  get: (id: string) =>
    apiClient.get(`/audit-logs/${id}`),
}

// Support API endpoints
export const supportApi = {
  sendRequest: (subject: string, message: string, attachment?: File) => {
    const formData = new FormData()
    formData.append('subject', subject)
    formData.append('message', message)
    if (attachment) {
      formData.append('attachment', attachment)
    }

    const token = localStorage.getItem('accessToken')
    return api.post('/auth/support-request', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }).then(res => res.data)
  },
}

// Admin API endpoints (root user only)
export const adminApi = {
  listOrganizations: (params?: {
    page?: number; limit?: number; search?: string; status?: AdminOrgStatus; hideDemo?: boolean
  }) => apiClient.get('/admin/organizations', params),

  getOrganization: (id: string) =>
    apiClient.get(`/admin/organizations/${id}`),

  getOrganizationUsers: (id: string) =>
    apiClient.getList(`/admin/organizations/${id}/users`),

  updateUserLimit: (id: string, freeSeats: number) =>
    apiClient.patch(`/admin/organizations/${id}/user-limit`, { freeSeats }),

  updateAiComplimentary: (id: string, aiComplimentary: boolean) =>
    apiClient.patch(`/admin/organizations/${id}/ai-complimentary`, { aiComplimentary }),

  getAiUsage: (id: string) =>
    apiClient.get(`/admin/organizations/${id}/ai-usage`),

  getSystemStats: () =>
    apiClient.get('/admin/stats'),

  paymentLogs: (params?: {
    page?: number; pageSize?: number; outcome?: string; authenticated?: string; search?: string; from?: string; to?: string; orgId?: string;
  }) => apiClient.get('/admin/payment-logs', params),

  // These two endpoints answer with the figures as the response body itself, not wrapped in
  // { data }, so they resolve to the body (apiClient.get would type a { data } envelope).
  overview: (): Promise<AdminOverview> =>
    api.get<AdminOverview>('/admin/overview').then(res => res.data),

  paymentLogStats: (since: string): Promise<PaymentLogStats> =>
    api.get<PaymentLogStats>('/admin/payment-logs/stats', { params: { since } }).then(res => res.data),
}

export default api
